// 🔧 event_defaults.js
// ✅ Purpose: Default event document template for LGV Demo Engine events
// ✅ Features:
// - Creates a consistent Firestore event document shape for demo events
// - Marks demo events with Demo Engine metadata
// - Supports temporary demo expiration fields for future customer demos
// - Keeps event defaults separate from Event Builder write logic
// - Fully block-commented for clarity and future devs

import {
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

const parseDateInput = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");

  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    12
  );

  if (
    date.getFullYear() !== Number(year) ||
    date.getMonth() !== Number(month) - 1 ||
    date.getDate() !== Number(day)
  ) {
    return null;
  }

  return date;
};

const getDemoExpirationDate = ({ daysFromNow = 14, hoursFromNow } = {}) => {
  const date = new Date();

  if (Number.isFinite(hoursFromNow)) {
    date.setHours(date.getHours() + hoursFromNow);
  } else {
    date.setDate(date.getDate() + daysFromNow);
  }

  return date;
};

/**
 * Builds a plain-text event description from the selected Experience manifest.
 *
 * @param {object} options Description inputs.
 * @return {string} Event-type-matched demo description.
 */
export const buildDemoEventDescription = ({
  eventName = "",
  industryTemplate = {},
} = {}) => {
  const eventType = String(industryTemplate?.label || "community event")
    .trim()
    .toLowerCase();
  const narrative = String(
    industryTemplate?.experience?.demoNarrative ||
      industryTemplate?.description ||
      ""
  )
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return [
    `${String(eventName).trim() || "This event"} is a ${eventType} demo Experience for customer walkthroughs.`,
    narrative,
  ]
    .filter(Boolean)
    .join(" ");
};

export const buildDemoEventDoc = ({
  user,
  demoConfig,
  industryTemplate,
  eventName,
  slug,
  experienceMetadata,
} = {}) => {
  const eventOwner = demoConfig?.eventOwner || {};
  const requiredDetails = [
    eventOwner.name,
    eventOwner.email,
    eventOwner.phone,
    demoConfig?.eventLocation,
  ];

  if (requiredDetails.some((value) => !String(value || "").trim())) {
    throw new Error(
      "Demo event owner name, email, phone, and event location are required."
    );
  }

  const startDate = parseDateInput(demoConfig?.eventStartDate);

  if (!startDate) {
    throw new Error("A valid demo event Start Date is required.");
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (demoConfig?.intent === "live_example" && startDate >= today) {
    throw new Error("Live Example Start Date must be in the past.");
  }

  const endDate = new Date(startDate);
  const description = buildDemoEventDescription({
    eventName,
    industryTemplate,
  });

  return {
    name: eventName,
    slug,
    description,
    ownerId: user?.uid || "",
    eventType: demoConfig?.industry || "car_show",
    entryLabel: industryTemplate?.entryLabel || "Entry",
    categoryLabel: industryTemplate?.categoryLabel || "Category",
    voterLabel: industryTemplate?.voterLabel || "Voter",
    eventLocation: demoConfig?.eventLocation || "",
    eventContact: {
      name: eventOwner.name,
      email: eventOwner.email,
      phone: eventOwner.phone,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),

    startDate: Timestamp.fromDate(startDate),
    endDate: Timestamp.fromDate(endDate),

    // =====================================================
    // ✅ Experience-specific voting engine
    // =====================================================
    // Purpose-built Experiences may require a fixed production
    // voting engine regardless of the generic Demo Builder size
    // or voting selector. Generic Experiences retain the current
    // demoConfig behavior.
    // =====================================================

    votingType:
      industryTemplate?.scoring?.votingType ||
      demoConfig?.votingEngine ||
      "points",

    // ✅ Root-event compatibility fallback for judged scoring.
    ...(Number.isInteger(
      industryTemplate?.scoring?.maxBallotPoints
    )
      ? {
          maxBallotPoints:
            industryTemplate.scoring.maxBallotPoints,
        }
      : {}),

    status: "Demo",

    paymentStatus: "demo",

    publicSeo: false,

    // Demo Experiences are available through their direct URL but should not
    // appear in public discovery or search-engine indexing.
    privateEvent: true,

    // ✅ Judge/manager/admin-only Experiences must not advertise
    // public leaderboard visibility through the Legacy root field.
    showLeaderboard:
      industryTemplate?.scoring?.resultsAudience
        ? industryTemplate.scoring.resultsAudience === "voters"
        : true,

    showWinners: true,
    winnersPublished: false,

    demo: true,
    demoEngineVersion: "1.0",
    demoIndustry: demoConfig?.industry || "car_show",
    demoIndustryLabel: industryTemplate?.label || "Car Show",
    demoIntent: demoConfig?.intent || "internal_testing",
    demoPackage: demoConfig?.package || "core",
    demoSize: demoConfig?.size || "small",
    demoTemporary: demoConfig?.expiration?.enabled === true,
    demoCreatedBy: user?.uid || "",
    demoCreatedAt: serverTimestamp(),
    demoSource: "platform_demo_builder",
    demoRunId: demoConfig?.runId || null,

    // ============================================================
    // Generic Experience Metadata Contract
    //
    // Purpose:
    // - Stores normalized industry metadata
    // - Keeps Experience Factory consumers generic
    // - Supports future industries
    // ============================================================

    experienceMetadata,

    demoExpiresAt:
      demoConfig?.expiration?.enabled === true
        ? Timestamp.fromDate(
            getDemoExpirationDate({
              daysFromNow: demoConfig?.expiration?.days || 14,
              hoursFromNow: demoConfig?.expiration?.hours,
            })
          )
        : null,

    lgvPackage: {
      type: "core",
      granted: true,
      grantedAt: serverTimestamp(),
      grantedBy: user?.uid || "",
      source: "demo_engine",
    },

    purchasedAddOns: {
      sponsors: demoConfig?.addOns?.sponsors === true,
      sponsorPayments: demoConfig?.addOns?.sponsors === true,
      volunteers: demoConfig?.addOns?.volunteers === true,
      vendors: demoConfig?.addOns?.vendors === true,
      vendorPayments: demoConfig?.addOns?.vendors === true,
      tickets: demoConfig?.addOns?.tickets === true,
      auction: demoConfig?.addOns?.auction === true,
      store: demoConfig?.addOns?.store === true,
      merch: demoConfig?.addOns?.store === true,
      passport: demoConfig?.addOns?.passport === true,
      social: demoConfig?.addOns?.social === true,
    },

    publicContent: {
      headline: `${eventName}`,
      intro:
        industryTemplate?.experience?.demoNarrative ||
        "This is a Let's Go Vote demo event created for testing, training, and customer walkthroughs.",
      participationTips:
        `Explore the ${String(industryTemplate?.entryLabel || "entry").toLowerCase()} choices, cast a demo vote as a ${String(industryTemplate?.voterLabel || "voter").toLowerCase()}, and review the results.`,
    },
  };
};
